// Helpers for guarding WebSocket-driven store requests against stuck loading
// states. When the WS connection drops mid-request, the server never replies
// and a store that sets `loadState: 'loading'` on dispatch would stay there
// forever — which surfaces as an infinite spinner or a disabled button with
// no feedback path.
//
// Usage:
//   const timers = createRequestTimers<'load' | 'save'>();
//   // on request dispatch:
//   timers.arm('load', 30_000, () => markLoadAsTimedOut());
//   // on response:
//   timers.clear('load');
//
// Per-key, so concurrent load + save requests don't clobber each other's
// timers. Latest call for the same key wins (replaces the prior timer).

import { NetworkError } from '@/lib/errors';

export const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;

export function timeoutError(operation: string): NetworkError {
  return new NetworkError(`${operation} timed out — connection may be down`);
}

export interface RequestTimers<K extends string> {
  arm(key: K, ms: number, onTimeout: () => void): void;
  clear(key: K): void;
  clearAll(): void;
}

export function createRequestTimers<K extends string>(): RequestTimers<K> {
  const handles = new Map<K, ReturnType<typeof setTimeout>>();
  return {
    arm(key, ms, onTimeout) {
      const existing = handles.get(key);
      if (existing) clearTimeout(existing);
      const handle = setTimeout(() => {
        handles.delete(key);
        onTimeout();
      }, ms);
      handles.set(key, handle);
    },
    clear(key) {
      const h = handles.get(key);
      if (h) {
        clearTimeout(h);
        handles.delete(key);
      }
    },
    clearAll() {
      for (const h of handles.values()) clearTimeout(h);
      handles.clear();
    },
  };
}
